import React from 'react'
import PropTypes from 'prop-types'
import { filterProps } from 'helpers/utils'
import styled, { css } from 'styled-components'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Popover from 'components/common/Popover'
import Link from '../common/Link/Link'
import GoBack from 'components/VehicleConfigurator/components/ConfirmTireSize/components/GoBack'
import OriginalEquipment from 'components/VehicleConfigurator/components/ConfirmTireSize/components/OriginalEquipment'
import MetaHelmet from '../IndexableYMME/components/MetaHelmet'
import SchemasHelmet from '../IndexableYMME/components/SchemasHelmet'
import {
  Instructions,
  PopoverLink,
  CustomPopover
} from 'components/VehicleConfigurator/components/ConfirmTireSize/components/OriginalTireSize/OriginalTireSize.styles'
import {
  FrontTires,
  BackTires,
  TireTitle,
  TireSize
} from 'components/VehicleConfigurator/components/ConfirmTireSize/components/ButtonStaggered'
import {
  Wrapper,
  ConfirmationWrapper,
  SelectTireSizeDisclaimer
} from 'components/VehicleConfigurator/components/ConfirmTireSize/components/ConfirmTireSize.styles'
import {
  TireSizeListWrapper,
  Wrapper as SizeWrapper
} from 'components/VehicleConfigurator/components/ConfirmTireSize/components/TireSizeList/TireSizeList.styles'

import CustomTireSize from 'components/VehicleConfigurator/components/ConfirmTireSize/components/CustomTireSize'

import {
  STAGGERED_TOOLTIP_TITLE_DEFAULT,
  STAGGERED_TOOLTIP_DESCRIPTION_DEFAULT
} from './constants'

const BOTSizeSelector = ({
  title,
  description,
  vehicleName,
  staggeredTireSizes = [],
  nonStaggeredTireSizes = [],
  instructions,
  isStaggered,
  aboutStaggeredFitmentDescription = STAGGERED_TOOLTIP_DESCRIPTION_DEFAULT, // TODO: BE To add to Response - BOT-874
  aboutStaggeredFitment = STAGGERED_TOOLTIP_TITLE_DEFAULT, // TODO: BE To add to Response - BOT-874
  metaDescription,
  metaTitle,
  imageSrc,
  canonicalUrl,
  vehicle,
  schemasName,
  ...props
}) =>
  <Wrapper data-at-confirm-tire-size>
    <MetaHelmet
      metaTitle={ metaTitle }
      metaDescription={ metaDescription }
      imageSrc={ imageSrc }
      canonicalUrl={ canonicalUrl } />
    <SchemasHelmet
      make={ vehicle.makeDesc }
      model={ vehicle.modelDesc }
      year={ vehicle.yearDesc }
      submodel={ vehicle.subModelDesc }
      imageSrc={ imageSrc }
      canonicalUrl={ canonicalUrl }
      schemasName={ schemasName }
      description={ metaDescription } />

    <GoBack />
    <Title headline centered bolder data-at-title>{ `${title} for Your ${vehicleName}`}</Title>
    <SelectTireSizeDisclaimer>Shopping for tires? Select tire size to proceed.</SelectTireSizeDisclaimer>
    <ConfirmationWrapper>
      {
        instructions &&
        <Instructions data-at-confirm-tire-size-instructions>
          { instructions }
          {
            isStaggered &&
            <Popover
              components={{ PopoverLink }}
              title={ aboutStaggeredFitment }
              info={ aboutStaggeredFitmentDescription }
              { ...props } />
          }
        </Instructions>
      }
      <TireSizeListWrapper>
        {
          nonStaggeredTireSizes.map(({
            diameter,
            tireFrontSize,
            searchTiresUrl
          }, index) =>
            <SizeWrapper key={ index }>
              <OriginalEquipment diameter={ diameter } />
              <TireSizeLink
                to={ `/tires/by-vehicle${searchTiresUrl}` }
                button
                followarrow
                block
                data-at-ymme-tiresize
                data-at-link-non-staggered>
                { tireFrontSize }
              </TireSizeLink>
            </SizeWrapper>
          )
        }
        {
          staggeredTireSizes.map(({
            diameter,
            rearDiameter,
            tireFrontSize,
            tireRearSize,
            searchTiresUrl
          }, index) =>
            <SizeWrapper key={ index }>
              <OriginalEquipment staggered diameter={ diameter } rearDiameter={ rearDiameter } />
              <TireSizeLink
                to={ `/tires/by-vehicle${searchTiresUrl}` }
                button
                followarrow
                block
                data-at-ymme-tiresize
                data-at-link-staggered
                staggered>
                <FrontTires>
                  <TireTitle>
                    Front Tires
                  </TireTitle>
                  <TireSize>
                    { tireFrontSize }
                  </TireSize>
                </FrontTires>
                <BackTires>
                  <TireTitle>
                    Rear Tires
                  </TireTitle>
                  <TireSize>
                    { tireRearSize }
                  </TireSize>
                </BackTires>
              </TireSizeLink>
            </SizeWrapper>
          )
        }
      </TireSizeListWrapper>
      <CustomPopover title="Where can I find my tire size?" />
      <StyledCustomTireSize { ...props } vehicle={ vehicle } />
    </ConfirmationWrapper>
  </Wrapper>

BOTSizeSelector.propTypes = {
  aboutStaggeredFitment: PropTypes.string,
  aboutStaggeredFitmentDescription: PropTypes.string,
  canonicalUrl: PropTypes.string,
  description: PropTypes.string,
  imageSrc: PropTypes.string,
  instructions: PropTypes.string,
  isStaggered: PropTypes.bool,
  metaDescription: PropTypes.string,
  metaTitle: PropTypes.string,
  nonStaggeredTireSizes: PropTypes.arrayOf(PropTypes.shape()),
  schemasName: PropTypes.string,
  staggeredTireSizes: PropTypes.arrayOf(PropTypes.shape()),
  title: PropTypes.string,
  vehicle: PropTypes.shape(),
  vehicleName: PropTypes.string
}

export default BOTSizeSelector

const TireSizeLink = styled(props => <Link { ...filterProps(props, ['staggered']) } />)`
  display: block;
  text-align: left;
  padding: 12px 30px 12px 20px;
  ${Mixin.font.GothamMedium(18, 24)}
  transform: none;
  ${props => props.staggered && css`
    ${Mixin.responsive('padding', '8px 44px 8px 20px', '12px 44px 0 18px')};
  `}

  & + & {
    margin-top: 10px;
  }
`


const StyledCustomTireSize = styled(CustomTireSize)`
  margin-top: 10px;
`

const Title = styled.h1`
  text-align: center;
  ${Mixin.font.GothamBold(24)}
  line-height: 50px;
  ${Media.mobile`
    ${Mixin.font.GothamBold(20)}
  `}
`
